interface CacheEntry<T> {
  expiresAt: number
  value: T
}

const STORAGE_PREFIX = 'mastermind-data-cache:'
const memoryCache = new Map<string, CacheEntry<unknown>>()

export const CACHE_TTL = {
  ATTENDANCE: 30 * 60 * 1000,
  PORTAL: 4 * 60 * 60 * 1000,
  REFERENCE: 24 * 60 * 60 * 1000
} as const

export const millisecondsUntilNextMidnight = () => {
  const now = new Date()
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
  }).formatToParts(now)
  const value = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find(part => part.type === type)?.value || 0)
  const elapsedToday = ((value('hour') * 60 + value('minute')) * 60 + value('second')) * 1000 + now.getMilliseconds()
  return Math.max(24 * 60 * 60 * 1000 - elapsedToday, 60_000)
}

const persistStorage = () => {
  try {
    return localStorage
  } catch {
    return null
  }
}

const currentScope = () => {
  let userId = 'anonymous'
  try {
    const auth = JSON.parse(localStorage.getItem('mastermind-auth') || '{}')
    userId = String(auth.user?.id || auth.state?.user?.id || 'anonymous')
  } catch {
    // A malformed auth cache is handled by the auth store; keep data isolated here.
  }
  return `${userId}:${localStorage.getItem('selectedSessionId') || 'active'}`
}

const scopedKey = (area: string, key: string) => `${area}:${currentScope()}:${key}`
const storageKey = (key: string) => `${STORAGE_PREFIX}${key}`

const readStorageItem = (key: string): string | null => {
  const persist = persistStorage()
  const persisted = persist?.getItem(storageKey(key))
  if (persisted) return persisted
  try {
    const sessionValue = sessionStorage.getItem(storageKey(key))
    if (sessionValue && persist) {
      persist.setItem(storageKey(key), sessionValue)
      sessionStorage.removeItem(storageKey(key))
    }
    return sessionValue
  } catch {
    return null
  }
}

const writeStorageItem = (key: string, value: string) => {
  const persist = persistStorage()
  persist?.setItem(storageKey(key), value)
  try {
    sessionStorage.removeItem(storageKey(key))
  } catch {
    // sessionStorage may already be gone on native WebView restart.
  }
}

const removeStorageItem = (key: string) => {
  persistStorage()?.removeItem(storageKey(key))
  try {
    sessionStorage.removeItem(storageKey(key))
  } catch {
    // Ignore missing sessionStorage during native process death.
  }
}

const parseEntry = <T>(raw: string | null): CacheEntry<T> | null => {
  if (!raw) return null
  try {
    const entry = JSON.parse(raw) as CacheEntry<T>
    if (!entry || typeof entry.expiresAt !== 'number') return null
    return entry
  } catch {
    return null
  }
}

const readEntry = <T>(key: string, allowExpired = false): CacheEntry<T> | null => {
  const memoryEntry = memoryCache.get(key) as CacheEntry<T> | undefined
  if (memoryEntry && (allowExpired || memoryEntry.expiresAt > Date.now())) return memoryEntry
  if (memoryEntry && !allowExpired) memoryCache.delete(key)

  const entry = parseEntry<T>(readStorageItem(key))
  if (!entry) {
    removeStorageItem(key)
    return null
  }
  if (!allowExpired && entry.expiresAt <= Date.now()) {
    memoryCache.delete(key)
    return null
  }
  memoryCache.set(key, entry)
  return entry
}

export function readCached<T>(area: string, key: string): T | null {
  const entry = readEntry<T>(scopedKey(area, key), true)
  return entry ? entry.value : null
}

export async function cachedRequest<T>(
  area: string,
  key: string,
  ttlMs: number,
  loader: () => Promise<T>,
  forceRefresh = false
): Promise<T> {
  const keyWithScope = scopedKey(area, key)
  if (!forceRefresh) {
    const cached = readEntry<T>(keyWithScope)
    if (cached) return cached.value
  }

  const value = await loader()
  const entry: CacheEntry<T> = { value, expiresAt: Date.now() + ttlMs }
  memoryCache.set(keyWithScope, entry)
  try {
    writeStorageItem(keyWithScope, JSON.stringify(entry))
  } catch {
    // Memory caching still works when browser storage is unavailable or full.
  }
  return value
}

export function invalidateCachedData(areas?: string[]) {
  const prefixes = areas?.length ? areas.map(area => `${area}:`) : []
  const matches = (key: string) => prefixes.length === 0 || prefixes.some(prefix => key.startsWith(prefix))

  for (const key of memoryCache.keys()) {
    if (matches(key)) memoryCache.delete(key)
  }

  const persist = persistStorage()
  const removeMatching = (storage: Storage | null) => {
    if (!storage) return
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const fullKey = storage.key(index)
      if (!fullKey?.startsWith(STORAGE_PREFIX)) continue
      const key = fullKey.slice(STORAGE_PREFIX.length)
      if (matches(key)) storage.removeItem(fullKey)
    }
  }

  removeMatching(persist)
  try {
    removeMatching(sessionStorage)
  } catch {
    // Native WebView may have already cleared sessionStorage.
  }
}
